import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useContext } from 'react';
import { useMutation } from '@apollo/client';
import { T, t } from '@sm/intl';
import { createUseStyles } from 'react-jss';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Input } from '@wds/input';
import { Modal } from '@wds/modal';
import { Grid } from '@wds/grid';
import { Typography } from '@wds/typography';
import { OECTrainingStatus } from '../../../types';
import { trackAmplitudeEvent, formatTheme, createKeywords } from '../../../lib';
import { validateTheme, NO_ERRORS } from '../../../validators';
import OpenEndedCodingContext from '../../../OpenEndedCodingContext';
import { useThemes, useGeneral, useThemesActions, useFilters, useFiltersActions } from '../../../Context';
import ThemeGuidanceCardColdStart from '../ThemeGuidanceCardColdStart';
import { useGeneralActions, useThemesReuse, useThemesReuseActions, useRetrainingActions } from '../../../Context';
import PreviouslyUsedThemes from './PreviouslyUsedThemes';
import SelectedThemes from './SelectedThemes';
import { createThemesForQuestion } from '../../../graph/createThemes';
export const COPY = {
  header: {
    id: 'OpenEndedCoding.AddThemeModalColdStart.header',
    defaultMessage: 'Create theme',
    description: '[Type: Header][Vis: med] - Header for Create Theme Modal'
  },
  cancel: {
    id: 'OpenEndedCoding.AddThemeModalColdStart.cancel',
    defaultMessage: 'Cancel',
    description: '[Type: Header][Vis: med] - Create Theme Modal cancel button'
  },
  create: {
    id: 'OpenEndedCoding.AddThemeModalColdStart.create',
    defaultMessage: 'Add {themes, plural, =1 {theme} =0 {theme} other {themes}}',
    description: '[Type: Header][Vis: med] - Create Theme Modal create button'
  },
  theme: {
    id: 'OpenEndedCoding.AddThemeModalColdStart.theme',
    defaultMessage: 'Theme name',
    description: '[Type: Header][Vis: med] - Create Theme Modal theme title'
  },
  placeholder: {
    id: 'OpenEndedCoding.AddThemeModalColdStart.placeholder',
    defaultMessage: 'Enter theme name',
    description: '[Type: Header][Vis: med] - Create Theme Modal placeholder'
  },
  inputLabel: {
    id: 'OpenEndedCoding.AddThemeModalColdStart.inputLabel',
    defaultMessage: 'Type theme name and press enter to confirm or quick add from below.',
    description: '[Type: Header][Vis: med] - Create Theme Modal input label'
  }
};
const useStyles = createUseStyles(() => ({
  leftPane: {
    maxHeight: '450px',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  badges: {
    display: 'inline-flex',
    flexWrap: 'wrap'
  }
}));
const AddThemeModalColdStart = ({
  show,
  createdSource,
  onClose
}) => {
  const classes = useStyles();
  const {
    setShouldRefetch
  } = useContext(OpenEndedCodingContext);
  const {
    surveyId,
    questionId,
    isSolutions
  } = useGeneral();
  const {
    themes,
    keywords
  } = useThemes();
  const {
    setMultiple: setMultipleFieldsThemes
  } = useThemesActions();
  const {
    selectedThemes
  } = useFilters();
  const {
    setSelectedThemes
  } = useFiltersActions();
  const {
    selectedReusedThemes
  } = useThemesReuse();
  const {
    addReusedTheme,
    setSelectedReusedThemes
  } = useThemesReuseActions();
  const {
    setError: setGeneralError
  } = useGeneralActions();
  const {
    setTrainingStatus
  } = useRetrainingActions();
  const [theme, setTheme] = useState('');
  const [error, setError] = useState(NO_ERRORS);
  const touched = useRef(false);
  const [createOECThemes] = useMutation(createThemesForQuestion, {
    onCompleted: ({
      createOecQuestionTags: {
        tags,
        trainingStatus: status
      }
    }) => {
      // error occurred
      if (!tags) {
        setGeneralError(true);
      }
      // themes created successfully, update state
      const formattedThemes = tags.map(tag => formatTheme(tag));
      setMultipleFieldsThemes({
        themes: [...themes, ...formattedThemes],
        keywords: [...keywords, ...formattedThemes.map(tag => createKeywords({
          theme: tag
        }))]
      });
      setSelectedThemes([...selectedThemes, formattedThemes.map(tag => tag.id)]);
      // we need to trigger a refetch for the data visualization
      setShouldRefetch(true);
      // user can enter a retraining state now
      if (status === OECTrainingStatus.REQUIRES_RETRAINING) {
        setTrainingStatus(status);
      }
    },
    onError: () => {
      setGeneralError(true);
    }
  });
  // handlers
  const handleThemeChange = e => {
    const themeName = e.currentTarget.value;
    setTheme(themeName);
    if (touched.current) {
      setError(validateTheme({
        themes,
        themeName
      }));
    }
  };
  const handleClose = () => {
    onClose();
    setError(NO_ERRORS);
    setTheme('');
  };
  const handleSubmit = e => {
    e.preventDefault();
    // catch any validation errors
    const validated = validateTheme({
      themes,
      themeName: theme
    });
    if (validated.error) {
      setError(validated);
      touched.current = true;
      return;
    }
    addReusedTheme({
      name: theme,
      suggested: false
    });
    setTheme('');
    setError(NO_ERRORS);
  };
  const handleCreateThemes = () => {
    // bulk create themes
    void createOECThemes({
      variables: {
        input: {
          surveyId,
          questionId,
          labels: selectedReusedThemes
        }
      }
    });
    setSelectedReusedThemes([]);
    onClose();
    setTheme('');
    // tracking
    trackAmplitudeEvent({
      amplitudeEvent: 'open-ended coding created theme',
      data: {
        surveyId,
        questionId,
        pageName: isSolutions ? 'ctm' : 'analyze',
        createdSource
      }
    });
  };
  return _jsxs(Modal, {
    "data-testid": "AddThemeModalColdStart__OEC",
    show: show,
    onClose: handleClose,
    size: "lg",
    children: [_jsx(Modal.Header, {
      header: t(COPY.header),
      stretched: true
    }), _jsx(Modal.Body, {
      children: _jsxs(Grid, {
        noBleeds: true,
        fluid: true,
        children: [_jsx(Grid.Item, {
          xs: 12,
          sm: 12,
          md: 6,
          children: _jsxs(Box, {
            className: classes.leftPane,
            children: [_jsxs(Box, {
              mb: 4,
              mr: 4,
              children: [_jsx("form", {
                onSubmit: handleSubmit,
                children: _jsx(Input, Object.assign({
                  ariaLabel: t(COPY.header),
                  stretched: true,
                  label: _jsx(Typography, {
                    variant: "body",
                    weight: "medium",
                    children: _jsx(T, {
                      desc: COPY.theme
                    })
                  }),
                  value: theme,
                  placeholder: t(COPY.placeholder),
                  onChange: handleThemeChange
                }, error.error && {
                  color: 'warning',
                  errorMessage: _jsx(Box, {
                    children: error.messages.map(message => _jsx(Typography, {
                      component: "p",
                      variant: "bodySm",
                      color: "warning",
                      children: message
                    }, message))
                  })
                }))
              }), _jsx(Box, {
                mt: 1,
                children: _jsx(Typography, {
                  component: "span",
                  variant: "bodySm",
                  children: _jsx(T, {
                    desc: COPY.inputLabel
                  })
                })
              })]
            }), _jsx(Box, {
              mb: 4,
              children: _jsx(SelectedThemes, {})
            }), _jsx(Box, {
              mb: 4,
              children: _jsx(PreviouslyUsedThemes, {})
            })]
          })
        }), _jsx(Grid.Item, {
          xs: 12,
          sm: 12,
          md: 6,
          children: _jsx(ThemeGuidanceCardColdStart, {})
        })]
      })
    }), _jsxs(Modal.Footer, {
      align: "right",
      children: [_jsx(Button, {
        variant: "ghost",
        color: "secondary",
        onClick: handleClose,
        children: _jsx(T, {
          desc: COPY.cancel
        })
      }), _jsx(Button, {
        type: "submit",
        disabled: selectedReusedThemes.length === 0,
        onClick: handleCreateThemes,
        children: _jsx(T, {
          desc: COPY.create,
          values: {
            themes: selectedReusedThemes.length
          }
        })
      })]
    })]
  });
};
export default AddThemeModalColdStart;